import { useEffect, useState } from "react";
import {
  SolutionHistoryHeader,
  SolutionHistoryItemWrapper,
  SolutionHistoryListWrapper,
  SolutionHistoryTable,
  SolutionHistoryThead,
  SolutionHistoryWrapper,
} from "../../../history/style";
import {
  getMileageAllMemberLogs,
  MemberMileageAllLogResponse,
} from "../../../../../../api/member";
import { pubState } from "../../../../../../recoil/pub.atom";
import { useRecoilValue } from "recoil";
import { PaginationWrapper } from "../../../../../../component/CommonTable";
import PopupPagination from "../../../../../../component/Pagination/popupPagination";
import { Title } from "../../../../../../styles/page";
import { MemberSearchBox } from "../../../../../member/style/memberTable.style";
import DropBox from "../../../../../../component/DropBox";
import {
  formatDate,
  formatDateWithDote,
  getMileageHistoryDateRange,
} from "../../../../../../utils/date";
import {
  formatMileageLogType,
  formatMileageLogUnit,
} from "../../../../utils/settlement.util";
import { getMileageMenu } from "../../../../../../api/pub-product";
import { downloadExcel } from "../../../../../../utils/excel";

interface SolutionHistoryProps {
  onClose: () => void;
  dailyDate: string;
}
const SolutionAllMilageHistory = ({
  onClose,
  dailyDate,
}: SolutionHistoryProps) => {
  const pub = useRecoilValue(pubState);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pubProducts, setPubProducts] = useState<
    { name: string; value: number | null }[]
  >([]);
  const [list, setList] = useState<MemberMileageAllLogResponse[]>([]);
  const [dateFilter, setDateFilter] = useState<string>("DAY");
  const [searchMember, setSearchMember] = useState<string>("");
  const [isSearch, setIsSearch] = useState<boolean>(true);
  const [productId, setProductId] = useState<number | null>(null);

  const setLogList = async () => {
    if (!pub) return;
    setSearchMember("");
    const res = await getMileageAllMemberLogs(
      pub.id,
      dailyDate,
      dateFilter,
      currentPage,
      10,
      searchMember,
      productId,
    );
    if (res.payload) {
      setList(res.payload.list);
      setTotalPage(Math.max(1, Math.ceil(res.payload.total / 10)));
      setIsSearch(false);
    }
  };

  const getPubProducts = async () => {
    if (!pub) return;
    const res = await getMileageMenu(pub.id);
    const list = [{ name: "전체", value: null }];
    setPubProducts([
      ...list,
      ...res.map((item) => ({ name: item.name, value: item.id })),
    ]);
  };

  useEffect(() => {
    getPubProducts();
  }, []);

  useEffect(() => {
    setLogList();
  }, [dateFilter, isSearch, productId, currentPage]);

  const Header = [
    "날짜",
    "매장명",
    "닉네임",
    "구분",
    "메뉴",
    "마일리지 (M)",
    "비고",
  ];

  const dateFilterList = [
    {
      name: "오늘",
      value: "DAY",
    },
    {
      name: "이번주",
      value: "WEEK",
    },
    {
      name: "이번달",
      value: "MONTH",
    },
  ];

  const handleDownloadCSV = async () => {
    if (!pub) return;
    const limit = 1000;

    const data = await getMileageAllMemberLogs(
      pub.id,
      dailyDate,
      dateFilter,
      1,
      limit,
      searchMember,
      productId,
    );

    const totalPage = Math.ceil(data.payload.total / limit);
    const list = data.payload.list.map((item) => ({
      날짜: formatDate(item.date),
      매장명: item.pubName,
      닉네임: item.nickname,
      구분: formatMileageLogType(item.type),
      메뉴: item.menu ?? "-",
      "마일리지 (M)":
        formatMileageLogUnit(item.type) + Number(item.point).toLocaleString(),
      비고: "-",
    }));
    const result = [...list];

    for (let i = 2; i <= totalPage; i++) {
      const res = await getMileageAllMemberLogs(
        pub.id,
        dailyDate,
        dateFilter,
        i,
        limit,
        searchMember,
        productId,
      );
      const list = res.payload.list.map((item) => ({
        날짜: formatDate(item.date),
        매장명: item.pubName,
        닉네임: item.nickname,
        구분: formatMileageLogType(item.type),
        메뉴: item.menu ?? "-",
        "마일리지 (M)":
          formatMileageLogUnit(item.type) + Number(item.point).toLocaleString(),
        비고: "-",
      }));

      result.push(...list);
    }

    const excelHeader = Header.map((item) => ({ header: item, key: item }));

    const { startDate, endDate } = getMileageHistoryDateRange(
      dailyDate,
      dateFilter,
    );

    const title = `${pub.pubName} 주류마일리지 내역(${formatDateWithDote(startDate)} ~ ${formatDateWithDote(endDate)})`;

    downloadExcel(excelHeader, result, title);
  };

  return (
    <SolutionHistoryWrapper>
      <SolutionHistoryHeader>
        <Title>주류마일리지 내역</Title>
        <div className="left">
          <MemberSearchBox>
            <input
              type="text"
              placeholder="닉네임을 검색하세요. "
              value={searchMember}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  setIsSearch(true);
                }
              }}
              onChange={(e) => {
                setCurrentPage(1);
                setSearchMember(e.target.value);
              }}
            />
            <div className="searchIcon" onClick={() => setIsSearch(true)}>
              <img
                src="/image-web/timer-config/searchIcon.svg"
                alt="검색 아이콘"
              />
            </div>
          </MemberSearchBox>
          <DropBox
            list={dateFilterList}
            value={dateFilter}
            setValue={setDateFilter}
          />
          <DropBox
            list={pubProducts}
            value={productId}
            setValue={setProductId}
            label="메뉴"
          />
          <div className="button" onClick={handleDownloadCSV}>
            CSV 다운로드
          </div>
          <img
            src="/image-web/solution/close.svg"
            alt="close"
            onClick={() => onClose()}
          />
        </div>
      </SolutionHistoryHeader>
      <SolutionHistoryTable>
        <SolutionHistoryThead>
          {Header.map((item, index) => (
            <div className={`item i${index}`}>{item}</div>
          ))}
        </SolutionHistoryThead>
        <SolutionHistoryListWrapper>
          {list.length > 0 ? (
            list.map((item, index) => (
              <SolutionHistoryItemWrapper key={index}>
                <div className="item i0">{formatDate(item.date, true)}</div>
                <div className="item i1">
                  <span>{item.pubName}</span>
                </div>
                <div className="item i2">
                  <span>{item.nickname}</span>
                </div>
                <div className="item i3">{formatMileageLogType(item.type)}</div>
                <div className="item i4">{item.menu ?? "-"}</div>
                <div className="item i5">
                  {formatMileageLogUnit(item.type)}
                  {Number(item.point).toLocaleString()}
                </div>
                <div className="item i6">-</div>
              </SolutionHistoryItemWrapper>
            ))
          ) : (
            <div className="no-data">내역이 없습니다. </div>
          )}
        </SolutionHistoryListWrapper>
      </SolutionHistoryTable>
      {totalPage > 1 && (
        <PaginationWrapper>
          <PopupPagination
            currentPage={currentPage}
            totalPages={totalPage}
            onPageChange={setCurrentPage}
          />
        </PaginationWrapper>
      )}
    </SolutionHistoryWrapper>
  );
};

export default SolutionAllMilageHistory;
